import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ShieldCheck, ShieldOff, ShieldAlert, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("hr-HR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [actingId, setActingId] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", role: "staff" });
  const [inviteBusy, setInviteBusy] = useState(false);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      setError(readError(err, "Could not load users."));
    }
  };

  useEffect(() => { load(); }, []);

  const forceDisable = async (id) => {
    setActingId(id);
    try {
      await api.post(`/admin/users/${id}/force-disable-2fa`);
      toast.success("2FA forcibly disabled.");
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not force-disable 2FA."));
    } finally {
      setActingId("");
    }
  };

  const sendInvite = async () => {
    setInviteBusy(true);
    try {
      await api.post("/admin/users/invite", invite);
      toast.success(`Invitation sent to ${invite.email}. Link printed to backend stdout (valid 24h).`);
      setInvite({ email: "", role: "staff" });
      setInviteOpen(false);
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not send invite."));
    } finally {
      setInviteBusy(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Administration</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Users</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            All accounts with access to the CMS. Force-disable 2FA only as a last resort — the action is audit-logged.
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" className="shadow-interactive">
              <UserPlus className="h-4 w-4" strokeWidth={1.5} /> Invite user
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-serif">Invite a new user</DialogTitle>
              <DialogDescription>
                Generates a 24-hour setup link printed to the backend stdout. SMTP isn't configured yet — share the link manually.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={invite.email} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} className="h-10 bg-background" data-testid="invite-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={invite.role} onValueChange={(v) => setInvite((i) => ({ ...i, role: v }))}>
                  <SelectTrigger className="h-10 bg-background" data-testid="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff (limited)</SelectItem>
                    <SelectItem value="admin">Admin (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteBusy}>Cancel</Button>
              <Button onClick={sendInvite} disabled={inviteBusy || !invite.email} className="shadow-interactive" data-testid="invite-send">
                {inviteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Send invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      <Card className="bg-card border-border/60 shadow-ambient overflow-hidden">
        <CardContent className="p-0">
          {users === null ? (
            <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No users yet.</div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-muted/40 border-border/60">
                  <TableHead className="py-4 px-6 text-foreground/80">Email</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Role</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">2FA</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80">Last login</TableHead>
                  <TableHead className="py-4 px-6 text-foreground/80 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-border/60 hover:bg-muted/20">
                    <TableCell className="py-4 px-6 font-medium text-foreground">{u.email}</TableCell>
                    <TableCell className="py-4 px-6">
                      <Badge variant={u.role === "admin" ? "default" : "secondary"} className={u.role === "admin" ? "bg-primary text-primary-foreground" : ""}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 px-6">
                      {u.is_2fa_enabled ? (
                        <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
                          <ShieldCheck className="h-4 w-4 text-primary" strokeWidth={1.5} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                          <ShieldOff className="h-4 w-4" strokeWidth={1.5} /> Off
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-4 px-6 text-sm text-muted-foreground">{fmtDate(u.last_login)}</TableCell>
                    <TableCell className="py-4 px-6 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!u.is_2fa_enabled || actingId === u.id}
                            data-testid={`force-disable-btn-${u.id}`}
                            className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                          >
                            {actingId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.5} />}
                            Force-disable 2FA
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif">Disable 2FA for this user?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will wipe their TOTP secret and all recovery codes. They will have to set up 2FA again from scratch. This action is permanent and audit-logged.
                              <br /><br />
                              <strong className="text-foreground">{u.email}</strong>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="force-disable-cancel-btn">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid="force-disable-confirm-btn"
                              onClick={() => forceDisable(u.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Yes, disable 2FA
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
